import React from "react"
import { useSearchParams } from "react-router-dom"
import { Building2, CalendarClock, CreditCard, Settings2, ShieldCheck, Users } from "lucide-react"
import SettingsSecurity from "@/components/settings/blocks/SettingsSecurity.tsx"
import SettingsBilling from "@/components/settings/blocks/SettingsBilling.tsx"
import SettingsReport from "@/components/settings/blocks/SettingsReport.tsx"
import SettingsWorkspaceGeneral from "@/components/settings/blocks/SettingsWorkspaceGeneral.tsx"
import SettingsMembers from "@/components/settings/blocks/SettingsMembers.tsx"
import SettingsCompanies from "@/components/settings/blocks/SettingsCompanies.tsx"
import WorkspaceSwitcher from "@/components/settings/components/WorkspaceSwitcher"
import { SettingsNav, VbPageHeader } from "@/components/vb"
import type { SettingsNavGroup } from "@/components/vb/SettingsNav"
import { useAuth } from "@/context/AuthContext"

type PanelKey =
    | "general"
    | "members"
    | "companies"
    | "billing"
    | "reporting"
    | "security"

type PanelConfig = {
    label: string
    /** Page title above the card column. */
    title: string
    subtitle?: string
    icon: React.ReactNode
    /** Account-scoped panels hide the workspace switcher. */
    accountScoped?: boolean
    render: () => React.ReactNode
}

const PANELS: Record<PanelKey, PanelConfig> = {
    general: {
        label: "General",
        title: "Workspace settings",
        subtitle: "The billing and team tenant. Everyone here can access all of its companies.",
        icon: <Settings2 className="h-[15px] w-[15px]" />,
        render: () => <SettingsWorkspaceGeneral />,
    },
    members: {
        label: "Members",
        title: "Members",
        subtitle: "Who can get into this workspace, and what they're allowed to do.",
        icon: <Users className="h-[15px] w-[15px]" />,
        render: () => <SettingsMembers />,
    },
    companies: {
        label: "Companies",
        title: "Companies",
        subtitle: "Each company gets its own AI Trust Signal report and Virtual Buyer.",
        icon: <Building2 className="h-[15px] w-[15px]" />,
        render: () => <SettingsCompanies />,
    },
    billing: {
        label: "Billing",
        title: "Billing",
        subtitle: "Your plan, credits and invoices.",
        icon: <CreditCard className="h-[15px] w-[15px]" />,
        render: () => <SettingsBilling />,
    },
    reporting: {
        label: "Reporting",
        title: "Reporting",
        subtitle: "Schedule automatic report delivery to selected people.",
        icon: <CalendarClock className="h-[15px] w-[15px]" />,
        render: () => <SettingsReport />,
    },
    security: {
        label: "Security",
        title: "Security",
        subtitle: "Your sign-in details for this account.",
        icon: <ShieldCheck className="h-[15px] w-[15px]" />,
        accountScoped: true,
        render: () => <SettingsSecurity />,
    },
}

/** The pre-AITS-494 `?tab=` values, kept working — they are linked from the
 *  sidebar, the landing resolver and the report dashboard's upgrade CTAs. */
const LEGACY_TAB_ALIASES: Record<string, PanelKey> = {
    workspace: "general",
    report: "reporting",
    account: "security",
}

const WORKSPACE_ITEMS: PanelKey[] = ["general", "members", "companies", "billing", "reporting"]

const isPanelKey = (value: string): value is PanelKey =>
    Object.prototype.hasOwnProperty.call(PANELS, value)

/** Exported for tests: the legacy aliases are load-bearing (live links point at
 *  ?tab=billing / ?tab=workspace) and break silently if they regress. */
export const resolveTab = (raw: string | null): PanelKey => {
    if (!raw) return "general"
    if (isPanelKey(raw)) return raw
    // hasOwnProperty, not a bare lookup: `?tab=toString` would otherwise resolve
    // through Object.prototype and hand back a function, which then blows up on
    // PANELS[currentTab].
    return Object.prototype.hasOwnProperty.call(LEGACY_TAB_ALIASES, raw)
        ? LEGACY_TAB_ALIASES[raw]
        : "general"
}

const Settings = () => {
    const { user } = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()

    const isGoogleUser = user?.google_origin
    const requestedTab = resolveTab(searchParams.get("tab"))
    // Google-origin accounts have no password to change, so Security is hidden —
    // including on a direct ?tab=security link, which previously rendered the
    // password form with no way back to it.
    const currentTab: PanelKey =
        isGoogleUser && requestedTab === "security" ? "general" : requestedTab

    const handleTabChange = (tab: string) => {
        setSearchParams({ tab })
    }

    const groups: SettingsNavGroup[] = React.useMemo(() => {
        const result: SettingsNavGroup[] = [
            {
                label: "Workspace",
                items: WORKSPACE_ITEMS.map((key) => ({
                    value: key,
                    label: PANELS[key].label,
                    icon: PANELS[key].icon,
                })),
            },
        ]
        if (!isGoogleUser) {
            result.push({
                label: "Account",
                items: [
                    { value: "security", label: PANELS.security.label, icon: PANELS.security.icon },
                ],
            })
        }
        return result
    }, [isGoogleUser])

    if (!user) return null

    const panel = PANELS[currentTab]

    return (
        <div className="vb-page">
            <VbPageHeader
                title={panel.title}
                subtitle={panel.subtitle}
                actions={panel.accountScoped ? undefined : <WorkspaceSwitcher />}
            />
            <div className="vb-settings">
                <div className="vb-settings__rail">
                    <SettingsNav
                        groups={groups}
                        value={currentTab}
                        onSelect={handleTabChange}
                        ariaLabel="Settings"
                    />
                </div>
                <div className="vb-settings__content">{panel.render()}</div>
            </div>
        </div>
    )
}

export default Settings
